import { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Box } from '@rsos/components/capstone/base';
import useTF911LoggedInUser from '@rsos/hooks/talkjs/useTF911LoggedInUser';
import { PreconfiguredMessageInterface } from '@rsos/reduxDataStore/src/features/alerts/chat.types';
import { setConversationUnread } from '@rsos/reduxDataStore/src/features/conversations/conversationsSlice';
import {
  useGetConversationID,
  useHasUnreadConversationMessages,
  useIsConversationEnded,
} from '@rsos/reduxDataStore/src/features/conversations/utils';
import { StateWithEmergencyCalls } from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCallsSlice';
import { createTalkUser } from '@rsos/utils/talkJS';
import { getTalkInstances, useTalkJS } from '@rsos/utils/useTalkJS';
import useTalkJSTheme from '../../../../hooks/useTalkJSTheme';
import { PRECONFIG_REQUEST_LOCATION } from '../../../PreconfiguredMessages/AllMessages/utils';
import SuggestedResponses from '../../../PreconfiguredMessages/SuggestedResponses';
import RequestLocation from '../../../RequestLocation';
import { useChatContext } from '../ChatContext';
import { OutgoingTranslationWarning } from '../SharedChatUI/OutgoingTranslationWarning';
import { ChatContainer } from './ConversationsChatbox.styles';

interface ConversationsChatProps {
  setIsAllMessagesVisible: (value: boolean) => void;
  selectedPreconfiguredMessage: PreconfiguredMessageInterface | null;
  setSelectedPreconfiguredMessage: (
    value: PreconfiguredMessageInterface | null,
  ) => void;
  hasRequestLocationAccess: boolean;
  preconfiguredMessages: PreconfiguredMessageInterface[];
}

const ConversationsChat = ({
  setIsAllMessagesVisible,
  selectedPreconfiguredMessage,
  setSelectedPreconfiguredMessage,
  hasRequestLocationAccess,
  preconfiguredMessages,
}: ConversationsChatProps) => {
  const { isCollapsed } = useChatContext();
  const callerID = useSelector(
    (state: StateWithEmergencyCalls) => state.emergencyCalls.selectedCallerID,
  );
  const dispatch = useDispatch();
  const chatTheme = useTalkJSTheme(true);
  const conversationID = useGetConversationID(callerID);
  const isConversationEnded = useIsConversationEnded(callerID);
  const loggedInUser = useTF911LoggedInUser();
  const hasUnreadConversationMessages =
    useHasUnreadConversationMessages(callerID);

  const { shellRef } = useTalkJS({
    theme: chatTheme,
    user: loggedInUser,
    instanceID: 'main',
  });
  const { chat, session } = getTalkInstances(chatTheme);

  const hasSuggestedResponses =
    !isConversationEnded && !isCollapsed && conversationID;

  const handleViewClick = () => {
    setIsAllMessagesVisible(true);
  };

  const handleRequestLocation = (url: string) => {
    if (chat && !isConversationEnded && preconfiguredMessages) {
      const locationMessage = preconfiguredMessages.find(
        message => message.title === PRECONFIG_REQUEST_LOCATION,
      );

      const talkjsMessage = locationMessage?.body.replace(/\{\{.*?\}\}/g, url);
      if (talkjsMessage) {
        chat.messageField.setText(talkjsMessage);
      }
    }
  };

  // Set Conversation
  useEffect(() => {
    if (chat && session && conversationID) {
      const conversation = session.getOrCreateConversation(conversationID);

      conversation.setParticipant(createTalkUser(loggedInUser));
      chat.select(conversation);

      chat.messageField.setVisible(!isConversationEnded);
    }
  }, [
    chat,
    session,
    conversationID,
    isConversationEnded,
    loggedInUser,
    callerID,
  ]);

  // Mark chat as read
  useEffect(() => {
    if (hasUnreadConversationMessages) {
      dispatch(setConversationUnread({ callerID, isUnread: false }));
    }
  }, [hasUnreadConversationMessages, callerID, dispatch]);

  // Set messageField text
  useEffect(() => {
    if (chat && !isConversationEnded) {
      if (selectedPreconfiguredMessage) {
        chat.messageField.setText(selectedPreconfiguredMessage.body);
        setSelectedPreconfiguredMessage(null);
      }
    }
  }, [
    selectedPreconfiguredMessage,
    chat,
    isConversationEnded,
    setSelectedPreconfiguredMessage,
    dispatch,
  ]);

  return (
    <>
      <ChatContainer ref={shellRef} display={isCollapsed ? 'none' : 'flex'} />
      {!isConversationEnded && <OutgoingTranslationWarning />}
      {hasSuggestedResponses && (
        <Box width="100%" horizontal align="center">
          {hasRequestLocationAccess && (
            <RequestLocation onSuccess={handleRequestLocation} />
          )}
          <SuggestedResponses handleViewClick={handleViewClick} />
        </Box>
      )}
    </>
  );
};

export default ConversationsChat;
